export interface LocatieProject {
  /** Korte titel, bijv. "Dakkapel Dubbeldam". */
  titel: string;
  /** Wijk of straatniveau-aanduiding. */
  wijk: string;
  /** Wat er gedaan is. */
  beschrijving: string;
  /** Doorlooptijd, bijv. "3 werkdagen". */
  doorlooptijd: string;
}

export interface LocatiePrijs {
  /** Dienstnaam, bijv. "Dakkapel (2,4 m)". */
  dienst: string;
  /** Prijsindicatie, bijv. "€ 7.500 – € 11.000". */
  indicatie: string;
  /** Link naar de bijbehorende kostenpagina. */
  href?: "/dakkapel-kosten" | "/aanbouw-kosten" | "/kozijnen-kosten" | "/renovatie-kosten";
}

export interface LocatieStap {
  titel: string;
  beschrijving: string;
}

export interface LocatieContent {
  plaats: string;
  /** URL-vriendelijke naam, bijv. "dordrecht". */
  slug: string;
  title: string;
  description: string;
  socialTitle: string;
  socialDescription: string;
  /** Korte intro onder de H1. */
  intro: string;
  /** 2-3 body-paragrafen. */
  paragraphs: string[];
  /** Specifieke wijken of gebieden binnen de plaats. */
  wijken?: string[];
  /** Aangrenzende plaatsen die we ook bedienen. */
  nabijgelegen?: string[];
  /** Dienst-slugs die we op de pagina uitlichten. */
  featuredDiensten: string[];
  /** Voorbeeldprojecten uit deze plaats. */
  projecten?: LocatieProject[];
  /** Werkwijze/planning specifiek voor deze plaats. */
  planning?: LocatieStap[];
  /** Lokale prijsindicatie per dienst. */
  prijzen?: LocatiePrijs[];
  /** Toelichting onder de prijstabel. */
  prijsToelichting?: string;
  /** Reistijd/aanrijafstand vanaf onze werkplaats in Dordrecht. */
  reistijd?: string;
  /** Postcodegebieden die onder deze plaats vallen. */
  postcodes?: string;
  /** Geo-coördinaten van het centrum, voor structured data. */
  geo?: { lat: number; lng: number };
}

/** NAP-gegevens (Naam, Adres, Postcode/Plaats) — één bron voor structured data en UI. */
export const nap = {
  naam: "Fedor Timmerwerken",
  juridischeNaam: "Aannemersbedrijf Fedor",
  straat: "Sikkelstraat 50",
  postcode: "3319 LJ",
  plaats: "Dordrecht",
  land: "NL",
  landNaam: "Nederland",
  telefoon: "+31628130786",
  telefoonWeergave: "06 28 13 07 86",
  email: "info@fedortimmerwerken.nl",
  kvk: "86124398",
  openingstijden: "ma t/m vr 07:00 – 17:00",
  geo: { lat: 51.7913, lng: 4.6795 },
} as const;

const standaardPlanning = (plaats: string): LocatieStap[] => [
  {
    titel: "1. Adviesgesprek aan huis",
    beschrijving: `We komen binnen een week bij je langs in ${plaats}, nemen de situatie op en meten alles zelf in.`,
  },
  {
    titel: "2. Heldere offerte",
    beschrijving:
      "Binnen 5 werkdagen ontvang je een gespecificeerde offerte: materiaal, arbeid, afvoer en eventuele vergunningskosten.",
  },
  {
    titel: "3. Planning en vergunning",
    beschrijving: `Bij vergunningplichtige plannen verzorgen we de aanvraag bij de gemeente. Daarna leggen we een startdatum vast en laten we weten hoe lang we in ${plaats} aan de slag zijn.`,
  },
  {
    titel: "4. Uitvoering",
    beschrijving:
      "We werken in vaste dagen door, houden de bouwplaats netjes en overleggen dagelijks over de voortgang.",
  },
  {
    titel: "5. Oplevering",
    beschrijving:
      "We lopen het werk samen na, werken restpunten direct af en laten de omgeving schoon achter.",
  },
];

export const locatieContent: Record<string, LocatieContent> = {
  dordrecht: {
    plaats: "Dordrecht",
    slug: "dordrecht",
    title: "Timmerman Dordrecht | Fedor Timmerwerken",
    description:
      "Op zoek naar een timmerman in Dordrecht? Fedor Timmerwerken maakt dakkapellen, kozijnen, aanbouwen en renovaties op maat. Gratis adviesgesprek aan huis.",
    socialTitle: "Timmerman Dordrecht | Fedor Timmerwerken",
    socialDescription:
      "Dakkapellen, kozijnen, aanbouwen en renovaties op maat in Dordrecht. Vraag een vrijblijvende offerte aan.",
    intro:
      "Fedor Timmerwerken is een lokaal timmer- en aannemersbedrijf uit Dordrecht. We werken dagelijks in de stad en de omliggende Drechtsteden, van klein timmerwerk tot complete verbouwingen.",
    paragraphs: [
      "Of je nu een dakkapel wilt plaatsen, je kozijnen wilt vervangen, een aanbouw wilt realiseren of je woning wilt renoveren: we maken alles ter plekke op maat. Dat betekent dat het resultaat altijd past bij jouw huis, jouw wensen en het karakter van de buurt.",
      "We komen graag bij je langs voor een gratis adviesgesprek. Tijdens dat gesprek bekijken we de situatie, bespreken we de mogelijkheden en maken we indien mogelijk direct een eerste inschatting van de kosten.",
      "Dordrecht kent veel jaren-30 woningen, vooroorlogse panden in de binnenstad en ruimere woningen in Dubbeldam en Stadspolders. Elk type vraagt om een andere aanpak — bij monumentale panden in de binnenstad werken we bijvoorbeeld met houten kozijnen en een aangepaste vergunningsroute.",
    ],
    wijken: [
      "Het Reeland",
      "Crabbehof",
      "Dubbeldam",
      "Zuidhoven",
      "Stadspolders",
      "Wielwijk",
      "Sterrenburg",
      "Binnenstad",
      "Krispijn",
    ],
    nabijgelegen: ["Zwijndrecht", "Papendrecht", "Sliedrecht"],
    featuredDiensten: ["dakkapel", "kozijnen", "aanbouw", "renovatie"],
    reistijd: "Onze werkplaats staat in Dordrecht-Zuid, dus we zijn overal in de stad binnen 15 minuten.",
    postcodes: "3311 t/m 3329",
    geo: { lat: 51.8133, lng: 4.669 },
    projecten: [
      {
        titel: "Dakkapel op jaren-30 woning",
        wijk: "Dubbeldam",
        beschrijving:
          "Dakkapel van 3,2 meter met kunststof kozijnen en zink zijwanden, geplaatst op een jaren-30 woning. Ruimte voor een volwaardige extra slaapkamer.",
        doorlooptijd: "3 werkdagen",
      },
      {
        titel: "Uitbouw achterzijde 18 m²",
        wijk: "Stadspolders",
        beschrijving:
          "HSB-uitbouw met plat dak, stalen pui en vloerverwarming. Keuken en tuin lopen nu in elkaar over.",
        doorlooptijd: "6 weken",
      },
      {
        titel: "Kozijnen vervangen binnenstad",
        wijk: "Binnenstad",
        beschrijving:
          "Acht houten kozijnen vervangen met HR++ glas, in overleg met de gemeente vanwege het beschermd stadsgezicht.",
        doorlooptijd: "2 weken",
      },
      {
        titel: "Volledige renovatie tussenwoning",
        wijk: "Krispijn",
        beschrijving:
          "Casco gestript, nieuwe indeling, vloeren, kozijnen en isolatie. Woning van energielabel F naar B.",
        doorlooptijd: "10 weken",
      },
    ],
    prijzen: [
      { dienst: "Dakkapel (2,5 – 3 m)", indicatie: "€ 7.500 – € 12.000", href: "/dakkapel-kosten" },
      { dienst: "Aanbouw / uitbouw per m²", indicatie: "€ 1.800 – € 3.200", href: "/aanbouw-kosten" },
      { dienst: "Kozijn vervangen (per stuk)", indicatie: "€ 800 – € 1.900", href: "/kozijnen-kosten" },
      { dienst: "Renovatie per m² woonoppervlak", indicatie: "€ 400 – € 900", href: "/renovatie-kosten" },
    ],
    prijsToelichting:
      "Prijsindicaties voor Dordrecht in 2026, inclusief materiaal en arbeid en exclusief btw en eventuele vergunningskosten. In de binnenstad liggen prijzen iets hoger door beperkte aanvoerruimte en eisen vanuit het beschermd stadsgezicht.",
    planning: standaardPlanning("Dordrecht"),
  },
  zwijndrecht: {
    plaats: "Zwijndrecht",
    slug: "zwijndrecht",
    title: "Timmerman Zwijndrecht | Fedor Timmerwerken",
    description:
      "Vakkundige timmerman in Zwijndrecht voor dakkapellen, kozijnen, aanbouw en renovatie. Fedor Timmerwerken werkt op maat en houdt zich aan de afspraak.",
    socialTitle: "Timmerman Zwijndrecht | Fedor Timmerwerken",
    socialDescription:
      "Dakkapellen, kozijnen, aanbouw en renovatie in Zwijndrecht. Op maat gemaakt en duidelijk in communicatie.",
    intro:
      "Fedor Timmerwerken is ook regelmatig in Zwijndrecht te vinden voor timmerwerk en verbouwingen. Van een enkele dakkapel tot een complete aanbouw: we werken met dezelfde zorg als in onze thuisstad Dordrecht.",
    paragraphs: [
      "We begrijpen dat je niet alleen een vakman zoekt, maar ook iemand die communiceert, afspraken nakomt en netjes achterlaat. Dat is precies waar we ons in onderscheiden.",
      "Veel woningen in Zwijndrecht komen uit de jaren 60 en 70. Daar valt met een dakkapel of uitbouw vaak flink wat woonruimte bij te winnen, en tegelijk kunnen we de isolatie meteen op niveau brengen.",
      "Wil je weten wat we voor jouw woning in Zwijndrecht kunnen betekenen? Neem contact op voor een vrijblijvende offerte of plan direct een adviesgesprek aan huis.",
    ],
    wijken: ["Walburg", "Nederhoven", "Kort Ambacht", "Heer Oudelands Ambacht", "Centrum", "Noord"],
    nabijgelegen: ["Dordrecht", "Papendrecht", "Sliedrecht"],
    featuredDiensten: ["dakkapel", "kozijnen", "aanbouw", "renovatie"],
    reistijd: "Zwijndrecht ligt op zo'n 10 minuten rijden van onze werkplaats in Dordrecht.",
    postcodes: "3331 t/m 3336",
    geo: { lat: 51.8189, lng: 4.6317 },
    projecten: [
      {
        titel: "Dakkapel jaren-70 woning",
        wijk: "Walburg",
        beschrijving:
          "Dakkapel van 2,4 meter met extra isolatie in de zijwanden, waardoor de zolder een werkkamer werd.",
        doorlooptijd: "2 werkdagen",
      },
      {
        titel: "Aanbouw met tuinkamer 14 m²",
        wijk: "Kort Ambacht",
        beschrijving: "Uitbouw met houtskeletbouw, grote schuifpui en plat dak met daklichten.",
        doorlooptijd: "5 weken",
      },
      {
        titel: "Kozijnen en voordeur vervangen",
        wijk: "Nederhoven",
        beschrijving: "Zes kozijnen en de voordeur vervangen met HR++ glas en driepuntssluiting.",
        doorlooptijd: "1,5 week",
      },
    ],
    prijzen: [
      { dienst: "Dakkapel (2,5 – 3 m)", indicatie: "€ 7.250 – € 11.500", href: "/dakkapel-kosten" },
      { dienst: "Aanbouw / uitbouw per m²", indicatie: "€ 1.750 – € 3.000", href: "/aanbouw-kosten" },
      { dienst: "Kozijn vervangen (per stuk)", indicatie: "€ 750 – € 1.800", href: "/kozijnen-kosten" },
      { dienst: "Renovatie per m² woonoppervlak", indicatie: "€ 400 – € 850", href: "/renovatie-kosten" },
    ],
    prijsToelichting:
      "Indicaties voor Zwijndrecht in 2026, inclusief materiaal en arbeid, exclusief btw en vergunningskosten. Ruimere achtertuinen en goede bereikbaarheid houden de kosten hier iets lager dan in de Dordtse binnenstad.",
    planning: standaardPlanning("Zwijndrecht"),
  },
  papendrecht: {
    plaats: "Papendrecht",
    slug: "papendrecht",
    title: "Timmerman Papendrecht | Fedor Timmerwerken",
    description:
      "Timmerman in Papendrecht gezocht? Fedor Timmerwerken bouwt dakkapellen, kozijnen, aanbouwen en renovaties op maat in Papendrecht en omgeving.",
    socialTitle: "Timmerman Papendrecht | Fedor Timmerwerken",
    socialDescription:
      "Dakkapellen, kozijnen, aanbouw en renovatie op maat in Papendrecht. Vraag een vrijblijvende offerte aan.",
    intro:
      "Ook in Papendrecht kunnen we je helpen met timmerwerk en verbouwprojecten. We rijden vanuit Dordrecht regelmatig naar Papendrecht voor opnames, overleg en uitvoering.",
    paragraphs: [
      "Of het nu gaat om nieuwe kozijnen met HR++ glas, een dakkapel op maat of een complete uitbouw: we denken met je mee en zorgen dat het plan past bij jouw woning en budget.",
      "In wijken als Wilgendonk en Oostpolder staan veel eengezinswoningen met een diepe tuin — ideaal voor een uitbouw van 12 tot 20 m². In de oudere delen van Papendrecht kiezen we vaker voor kozijnvervanging en dakkapellen.",
      "Neem contact met ons op voor een adviesgesprek aan huis. We bekijken de situatie ter plekke en stellen daarna een heldere offerte op.",
    ],
    wijken: ["Wilgendonk", "Oostpolder", "Molenvliet", "Kraaihoek", "Centrum", "Westpolder"],
    nabijgelegen: ["Dordrecht", "Zwijndrecht", "Sliedrecht"],
    featuredDiensten: ["dakkapel", "kozijnen", "aanbouw", "renovatie"],
    reistijd: "Papendrecht bereiken we in ongeveer 15 minuten vanaf onze werkplaats in Dordrecht.",
    postcodes: "3350 t/m 3356",
    geo: { lat: 51.8317, lng: 4.6903 },
    projecten: [
      {
        titel: "Uitbouw 16 m² met keukenwand",
        wijk: "Wilgendonk",
        beschrijving:
          "Uitbouw over de volle breedte met vloerverwarming en een schuifpui van 4 meter richting de tuin.",
        doorlooptijd: "6 weken",
      },
      {
        titel: "Dakkapel met dakraam",
        wijk: "Molenvliet",
        beschrijving: "Dakkapel van 2,8 meter aan de achterzijde, gecombineerd met een dakraam aan de voorzijde.",
        doorlooptijd: "3 werkdagen",
      },
      {
        titel: "Veranda en vlonder",
        wijk: "Oostpolder",
        beschrijving: "Overkapping van 5 x 3 meter in douglashout met hardhouten vlonder.",
        doorlooptijd: "1 week",
      },
    ],
    prijzen: [
      { dienst: "Dakkapel (2,5 – 3 m)", indicatie: "€ 7.500 – € 11.750", href: "/dakkapel-kosten" },
      { dienst: "Aanbouw / uitbouw per m²", indicatie: "€ 1.800 – € 3.100", href: "/aanbouw-kosten" },
      { dienst: "Kozijn vervangen (per stuk)", indicatie: "€ 800 – € 1.850", href: "/kozijnen-kosten" },
      { dienst: "Renovatie per m² woonoppervlak", indicatie: "€ 400 – € 900", href: "/renovatie-kosten" },
    ],
    prijsToelichting:
      "Indicaties voor Papendrecht in 2026, inclusief materiaal en arbeid, exclusief btw en vergunningskosten. Wijken met een diepe achtertuin lenen zich goed voor een grotere uitbouw, wat de prijs per m² juist verlaagt.",
    planning: standaardPlanning("Papendrecht"),
  },
  sliedrecht: {
    plaats: "Sliedrecht",
    slug: "sliedrecht",
    title: "Timmerman Sliedrecht | Fedor Timmerwerken",
    description:
      "Op zoek naar een betrouwbare timmerman in Sliedrecht? Fedor Timmerwerken realiseert dakkapellen, kozijnen, aanbouwen en renovaties op maat.",
    socialTitle: "Timmerman Sliedrecht | Fedor Timmerwerken",
    socialDescription:
      "Dakkapellen, kozijnen, aanbouw en renovatie in Sliedrecht. Op maat gemaakt met duidelijke afspraken.",
    intro:
      "Sliedrecht ligt om de hoek van Dordrecht en hoort bij het werkgebied van Fedor Timmerwerken. We komen graag naar Sliedrecht voor timmerwerk, verbouwingen en maatoplossingen.",
    paragraphs: [
      "Van het vervangen van kozijnen tot het plaatsen van een dakkapel of het realiseren van een aanbouw: we werken netjes, snel en volgens afspraak.",
      "Sliedrecht heeft veel lintbebouwing langs de rivier en nieuwere wijken zoals Baanhoek-West. Bij de oudere woningen langs de dijk kijken we altijd extra naar de constructie voordat we een dakkapel of uitbouw inplannen.",
      "Wil je een vrijblijvende offerte of een adviesgesprek aan huis in Sliedrecht? Neem contact op, dan plannen we een moment in.",
    ],
    wijken: ["Baanhoek-West", "Baanhoek", "Centrum", "Oost", "Kerkbuurt", "Molendijk"],
    nabijgelegen: ["Dordrecht", "Zwijndrecht", "Papendrecht"],
    featuredDiensten: ["dakkapel", "kozijnen", "aanbouw", "renovatie"],
    reistijd: "Sliedrecht ligt op ongeveer 20 minuten rijden vanaf onze werkplaats in Dordrecht.",
    postcodes: "3360 t/m 3363",
    geo: { lat: 51.8226, lng: 4.7743 },
    projecten: [
      {
        titel: "Dakkapel nieuwbouwwoning",
        wijk: "Baanhoek-West",
        beschrijving: "Dakkapel van 3 meter met kunststof kozijnen, passend binnen de welstandseisen van de wijk.",
        doorlooptijd: "3 werkdagen",
      },
      {
        titel: "Uitbouw dijkwoning 12 m²",
        wijk: "Molendijk",
        beschrijving:
          "Uitbouw met extra fundering vanwege de dijkligging, inclusief nieuwe pui en isolatie van het bestaande dak.",
        doorlooptijd: "7 weken",
      },
      {
        titel: "Schuur met overkapping",
        wijk: "Oost",
        beschrijving: "Vrijstaande schuur van 4 x 3 meter met aangebouwde overkapping in geïmpregneerd hout.",
        doorlooptijd: "1,5 week",
      },
    ],
    prijzen: [
      { dienst: "Dakkapel (2,5 – 3 m)", indicatie: "€ 7.500 – € 12.000", href: "/dakkapel-kosten" },
      { dienst: "Aanbouw / uitbouw per m²", indicatie: "€ 1.850 – € 3.200", href: "/aanbouw-kosten" },
      { dienst: "Kozijn vervangen (per stuk)", indicatie: "€ 800 – € 1.900", href: "/kozijnen-kosten" },
      { dienst: "Renovatie per m² woonoppervlak", indicatie: "€ 425 – € 900", href: "/renovatie-kosten" },
    ],
    prijsToelichting:
      "Indicaties voor Sliedrecht in 2026, inclusief materiaal en arbeid, exclusief btw en vergunningskosten. Bij dijkwoningen kan extra fundering of constructieadvies nodig zijn; dat rekenen we altijd apart en vooraf door.",
    planning: standaardPlanning("Sliedrecht"),
  },
};
